import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { X, Plus, Loader2 } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { ASSIGN } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

export default function AssignmentsPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedSpaces, setSelectedSpaces] = useState({});
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/assignments');
            setData(data);
            if (!selectedUserId && data.assignments.length > 0) {
                const firstUser = data.assignments.find((a) => a.user.role !== 'admin') || data.assignments[0];
                setSelectedUserId(firstUser.user.id);
            }
        } finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    const selectedEntry = useMemo(
        () => data?.assignments.find((a) => a.user.id === selectedUserId),
        [data, selectedUserId],
    );
    const assignedIds = new Set((selectedEntry?.assigned_spaces || []).map((s) => s.id));
    const available = (data?.all_spaces || []).filter((s) => !assignedIds.has(s.id));

    const toggleSpace = (id) => setSelectedSpaces((s) => ({ ...s, [id]: !s[id] }));

    const submit = async () => {
        const ids = Object.entries(selectedSpaces).filter(([, v]) => v).map(([k]) => k);
        if (!selectedUserId || ids.length === 0) return;
        setSaving(true);
        try {
            await api.post('/assignments', { user_id: selectedUserId, space_ids: ids });
            toast.success(`Assigned ${ids.length} Space(s)`);
            setSelectedSpaces({}); load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const removeAssign = async (uid, sid) => {
        try {
            await api.delete('/assignments', { data: { user_id: uid, space_id: sid } });
            toast.success('Removed');
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div data-testid={ASSIGN.root} className="space-y-6">
            <div>
                <h1 className="font-display text-4xl font-black tracking-tight mt-1">Space Assignments</h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    Assign one or many Spaces to a user. Users only see and browse Spaces assigned to them.
                </p>
            </div>

            {loading || !data ? (
                <div className="text-center py-12 text-muted-foreground">Loading…</div>
            ) : (
                <div className="grid lg:grid-cols-3 gap-4">
                    {/* Assign panel */}
                    <div className="rounded-sm border border-border bg-card p-5 space-y-4">
                        <div>
                            <Label className="text-sm">Select user</Label>
                            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                                <SelectTrigger data-testid={ASSIGN.userSelect} className="rounded-sm mt-2">
                                    <SelectValue placeholder="Choose a user" />
                                </SelectTrigger>
                                <SelectContent className="bg-popover border-border">
                                    {data.assignments.map((a) => (
                                        <SelectItem key={a.user.id} value={a.user.id}>
                                            {a.user.name || a.user.email} · {a.user.role}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <div className="text-sm text-muted-foreground mb-2">Available Spaces</div>
                            {available.length === 0 ? (
                                <div className="text-xs text-muted-foreground py-4">
                                    All Spaces already assigned to this user.
                                </div>
                            ) : (
                                <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                                    {available.map((s) => (
                                        <label
                                            key={s.id}
                                            className="flex items-center gap-3 px-3 py-2 rounded-sm border border-border hover:bg-muted/40 cursor-pointer row-hover"
                                        >
                                            <Checkbox
                                                checked={!!selectedSpaces[s.id]}
                                                onCheckedChange={() => toggleSpace(s.id)}
                                                data-testid={ASSIGN.spaceCheckbox(s.id)}
                                                className="rounded-sm"
                                            />
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium truncate">{s.name}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {s.region} · {s.bucket}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        <Button
                            onClick={submit}
                            disabled={saving || !selectedUserId || Object.values(selectedSpaces).every((v) => !v)}
                            data-testid={ASSIGN.submit}
                            className="w-full rounded-sm bg-primary hover:bg-primary/90"
                        >
                            {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : <Plus size={14} className="mr-2" />}
                            Assign selected
                        </Button>
                    </div>

                    {/* Assignment matrix */}
                    <div className="lg:col-span-2 rounded-sm border border-border bg-card overflow-x-auto">
                        <table className="w-full text-sm table-tech">
                            <thead>
                                <tr className="border-b border-border">
                                    <th className="text-left px-4 py-3">User</th>
                                    <th className="text-left px-4 py-3">Role</th>
                                    <th className="text-left px-4 py-3">Assigned Spaces</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.assignments.map((a) => (
                                    <tr key={a.user.id} className="border-b border-border last:border-0">
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{a.user.name || '—'}</div>
                                            <div className="text-xs text-muted-foreground">{a.user.email}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="text-xs">{a.user.role}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                            {a.assigned_spaces.length === 0 ? (
                                                <span className="text-xs text-muted-foreground">— none —</span>
                                            ) : (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {a.assigned_spaces.map((s) => (
                                                        <button
                                                            key={s.id}
                                                            onClick={() => removeAssign(a.user.id, s.id)}
                                                            data-testid={ASSIGN.remove(a.user.id, s.id)}
                                                            className="group inline-flex items-center gap-1.5 rounded-sm border border-border px-2 py-1 text-xs bg-background hover:bg-destructive/10 hover:border-destructive/50 row-hover"
                                                            title="Remove assignment"
                                                        >
                                                            <span>{s.name}</span>
                                                            <X size={11} className="text-muted-foreground group-hover:text-destructive" />
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
